import { Dispatch, FC, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Nullable } from 'models/helpers'
import { StatusResponse } from 'models/redux'
import { VisualFileType } from 'constants/visual'
import { countPhotos, withPartner } from '../blurPairs'
import { mutationToAPIRequest } from 'utils/reactQuery/queryHelpers'
import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryConstants } from './GalleryConstants.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useGalleryVisuals } from './GalleryVisuals.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'
import { useSelectVisuals } from 'dataQueries'

interface GalleryVisualSelectionInterface {
  selected: Set<string>
  setSelected: Dispatch<SetStateAction<Set<string>>>
  canSelectVisuals: boolean
  selectableVisualsKeys: string[]
  selectableVisualsCount: number
  selectAll: () => void
  selectAllPurchased: () => void
  selectAllUnpurchased: () => void
  deselectAll: () => void
  toggleSelectOnImage: (filename?: string | undefined) => void
  selectedNotPurchasedVisualsKeys: Set<string>
  selectedPurchasedVisualsKeys: Set<string>
  selectedNotPurchasedVisualsCount: number
  selectedPurchasedVisualsCount: number
  /** Selected photos, both versions of a paired photo counted once */
  selectedPhotosCount: number
  selectVisualsRequest?: Nullable<StatusResponse>
  selectVisualsMutation: ReturnType<typeof useSelectVisuals>
}

const defaultGalleryVisualSelectionValue: GalleryVisualSelectionInterface = {
  selected: new Set(),
  setSelected: () => { throw new Error('setSelected is undefined') },
  canSelectVisuals: false,
  selectableVisualsKeys: [],
  selectableVisualsCount: 0,
  selectAll: () => { throw new Error('selectAll is undefined') },
  selectAllPurchased: () => { throw new Error('selectAllPurchased is undefined') },
  selectAllUnpurchased: () => { throw new Error('selectAllUnpurchased is undefined') },
  deselectAll: () => { throw new Error('deselectAll is undefined') },
  toggleSelectOnImage: () => { throw new Error('toggleSelectOnImage is undefined') },
  selectedNotPurchasedVisualsKeys: new Set(),
  selectedPurchasedVisualsKeys: new Set(),
  selectedNotPurchasedVisualsCount: 0,
  selectedPurchasedVisualsCount: 0,
  selectedPhotosCount: 0,
  selectVisualsRequest: undefined,
  selectVisualsMutation: {} as ReturnType<typeof useSelectVisuals>,
}

/** Gallery visual selection context */
export const GalleryVisualSelectionContext = createContext<GalleryVisualSelectionInterface>(defaultGalleryVisualSelectionValue)
/** Gallery visual selection context hook */
export const useGalleryVisualSelection = (): GalleryVisualSelectionInterface => useContext(GalleryVisualSelectionContext)

/** Context provider for gallery visual selection */
export const GalleryVisualSelectionContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { roles } = useAuth0()

    const {
      stagesUnlockingCreativeSendAssignmentVisualsToAdmin,
    } = useGalleryConstants()

    const {
      assignmentStage,
    } = useGalleryAssignment()

    const {
      thumbnailType,
      originalType,
    } = useGalleryVisualType()

    const {
      purchasedVisualsKeys,
      preselectedFilenames,
      unPurchasedVisualsKeys,
      blurPartners,
    } = useGalleryVisualsMeta()

    const {
      downloadVisuals,
      downloadVisualsKeys,
    } = useGalleryVisuals()

    const {
      isVirtualVisit,
    } = useGalleryProduct()

    const [selected, setSelected] = useState<Set<string>>(new Set())
    const canSelectVisuals = useMemo(() => !isVirtualVisit && (roles.isAdmin || roles.isClient) || (roles.isCreative && !!assignmentStage && stagesUnlockingCreativeSendAssignmentVisualsToAdmin.has(assignmentStage)), [isVirtualVisit, roles, assignmentStage, stagesUnlockingCreativeSendAssignmentVisualsToAdmin])
    const selectableVisualsKeys = useMemo(() => downloadVisualsKeys.filter(key => thumbnailType && downloadVisuals?.[key]?.[thumbnailType] && (roles.isAdmin || roles.isClient || (roles.isCreative && !!assignmentStage && stagesUnlockingCreativeSendAssignmentVisualsToAdmin.has(assignmentStage)))), [downloadVisualsKeys, thumbnailType, downloadVisuals, roles, assignmentStage, stagesUnlockingCreativeSendAssignmentVisualsToAdmin])
    const selectableVisualsCount = useMemo(() => selectableVisualsKeys.length, [selectableVisualsKeys])

    const selectedNotPurchasedVisualsKeys = useMemo(() => new Set([...selected].filter(visual => ![...purchasedVisualsKeys].includes(visual))), [selected, purchasedVisualsKeys])
    const selectedNotPurchasedVisualsCount = useMemo(() => countPhotos(selectedNotPurchasedVisualsKeys, blurPartners), [selectedNotPurchasedVisualsKeys, blurPartners])
    const selectedPhotosCount = useMemo(() => countPhotos(selected, blurPartners), [selected, blurPartners])

    const selectedPurchasedVisualsKeys = useMemo(() => new Set([...selected].filter(visual => [...purchasedVisualsKeys].includes(visual))), [selected, purchasedVisualsKeys])
    const selectedPurchasedVisualsCount = useMemo(() => selectedPurchasedVisualsKeys.size, [selectedPurchasedVisualsKeys])

    const selectVisualsMutation = useSelectVisuals()
    const selectVisualsRequest = useMemo(() => {
      const req = mutationToAPIRequest(selectVisualsMutation)
      return req.state === 'BEFORE_START' ? undefined : req
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectVisualsMutation.status, selectVisualsMutation.error]) as Nullable<StatusResponse>

    const selectAll = useCallback(() => {
      setSelected(new Set(selectableVisualsKeys))
    }, [selectableVisualsKeys, setSelected])

    const selectAllUnpurchased = useCallback(() => {
      if (!unPurchasedVisualsKeys) return
      else setSelected(unPurchasedVisualsKeys)
    }, [unPurchasedVisualsKeys])

    const selectAllPurchased = useCallback(() => {
      if (!!purchasedVisualsKeys) setSelected(new Set(purchasedVisualsKeys))
    }, [purchasedVisualsKeys, setSelected])

    const deselectAll = useCallback(() => {
      setSelected(new Set())
    }, [setSelected])

    // Picking or unpicking either version of a paired photo does the same to the other one
    const toggleSelectOnImage = useCallback((filename?: string) => {
      if (!filename) return
      const filenames = withPartner(filename, blurPartners)
      const next = new Set(selected)
      if (selected.has(filename)) filenames.forEach(name => next.delete(name))
      else filenames.forEach(name => next.add(name))
      setSelected(next)
    }, [selected, blurPartners])

    // Preselect all uploaded visuals for creative
    useEffect(() => {
      if (!roles.isCreative) return
      if (!canSelectVisuals) return
      if (selectableVisualsKeys.length === 0) return
      setSelected(new Set(selectableVisualsKeys))
    }, [roles, canSelectVisuals, selectableVisualsKeys])

    // Preselect all RAW visuals for creative and admin
    useEffect(() => {
      if (!(roles.isCreative || roles.isAdmin)) return
      if (originalType !== VisualFileType.RAW) return
      if (preselectedFilenames.length === 0) return
      setSelected(new Set(preselectedFilenames))
    }, [originalType, preselectedFilenames, roles])

    // Prune stale entries from selected that are no longer in downloadVisualsKeys
    useEffect(() => {
      if (selected.size === 0) return
      const downloadKeysSet = new Set(downloadVisualsKeys)
      const pruned = new Set(Array.from(selected).filter(key => downloadKeysSet.has(key)))
      if (pruned.size !== selected.size) setSelected(pruned)
    }, [selected, downloadVisualsKeys])

    // Reset selection when changing galleries
    useEffect(() => {
      if (selected.size) deselectAll()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId, deselectAll])

    return (
      <GalleryVisualSelectionContext.Provider
        value={{
          selected,
          setSelected,
          canSelectVisuals,
          selectableVisualsKeys,
          selectableVisualsCount,
          selectAll,
          selectAllPurchased,
          deselectAll,
          toggleSelectOnImage,
          selectAllUnpurchased,
          selectedNotPurchasedVisualsKeys,
          selectedNotPurchasedVisualsCount,
          selectedPurchasedVisualsCount,
          selectedPhotosCount,
          selectedPurchasedVisualsKeys,
          selectVisualsRequest,
          selectVisualsMutation,
        }}
      >
        {children}
      </GalleryVisualSelectionContext.Provider>
    )
  }
